import {
  ErrorBox,
  GET_METRIC_BY_METRIC_ID_AND_ENVS,
  GET_POLICY_BY_POLICY_ID,
  GovernanceTestOverviewMetric,
  GovernanceTestOverviewPolicy,
  roundWithoutApprossimation,
  toGovernanceMetric,
  toGovernancePolicy,
} from '@agilelab/plugin-wb-governance';
import {
  GovernanceEntityType,
  MetricByIdQuery,
  PoliciesByIdsQuery,
} from '@agilelab/plugin-wb-governance-common';
import {
  CodeField,
  GenericField,
  WbTooltip,
} from '@agilelab/plugin-wb-platform';
import { snakeCaseToTitleCase } from '@agilelab/plugin-wb-platform-common';
import { useQuery } from '@apollo/client';
import { ErrorPanel, Progress } from '@mainstage/core-components';
import {
  Box,
  Drawer,
  DrawerProps,
  makeStyles,
  Theme,
  Typography,
  useTheme,
} from '@material-ui/core';
import { format, parseISO } from 'date-fns';
import { useMemo, useState } from 'react';
import { DetailIcon } from '../TestTable/DetailIcon';
import { Task } from '../types';
import { MetricHeader } from './MetricHeader';
import { PolicyHeader } from './PolicyHeader';

const useStyles = makeStyles((_theme: Theme) => ({
  drawerPaper: {
    height: '100%',
    width: '50%',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  chip: {
    margin: 0,
  },
}));

const ErrorBoxWrapper = (props: { errors: string[] }) => {
  const { errors } = props;
  const [currentErrorIndex, setCurrentErrorIndex] = useState<number>(0);
  const handleSwitch = (index: number) => {
    setCurrentErrorIndex(index);
  };

  return (
    <ErrorBox
      error={{
        message: errors.at(currentErrorIndex)!,
      }}
      navigation={{
        current: currentErrorIndex,
        total: errors.length,
        handleSwitch: handleSwitch,
      }}
      headerTitle="Error Info"
    />
  );
};

export const TaskDrawer = (
  props: DrawerProps & {
    task: Task | null;
  },
) => {
  const { task, ...drawerProps } = props;
  const classes = useStyles();
  const theme = useTheme();

  const {
    data: policyData,
    error: policyError,
    loading: loadingPolicy,
  } = useQuery<PoliciesByIdsQuery>(GET_POLICY_BY_POLICY_ID, {
    variables: { policyId: task?.governanceEntityId },
    skip: task?.governanceEntityId
      ? task.governanceEntityType !== GovernanceEntityType.Policy
      : true,
  });

  const {
    data: metricData,
    error: metricError,
    loading: loadingMetric,
  } = useQuery<MetricByIdQuery>(GET_METRIC_BY_METRIC_ID_AND_ENVS, {
    variables: { metricId: task?.governanceEntityId },
    skip: task?.governanceEntityId
      ? task.governanceEntityType !== GovernanceEntityType.Metric
      : true,
  });

  // Replace Policy Status with the one returned from the test
  const policy = useMemo(() => {
    const fetchedPolicy =
      policyData &&
      policyData.cgp_governance_entity[0] &&
      toGovernancePolicy(policyData.cgp_governance_entity[0]);
    if (!fetchedPolicy) return fetchedPolicy;
    return {
      ...fetchedPolicy,
      ...(task?.governanceEntityStatus
        ? { status: task.governanceEntityStatus }
        : {}),
    };
  }, [policyData, task]);

  // Replace Metric Status with the one returned from the test
  const metric = useMemo(() => {
    const fetchedPolicy =
      metricData &&
      metricData.cgp_governance_entity[0] &&
      toGovernanceMetric(metricData.cgp_governance_entity[0]);
    if (!fetchedPolicy) return fetchedPolicy;
    return {
      ...fetchedPolicy,
      ...(task?.governanceEntityStatus
        ? { status: task.governanceEntityStatus }
        : {}),
    };
  }, [metricData, task]);

  return (
    <Drawer
      anchor="right"
      classes={{
        paper: classes.drawerPaper,
      }}
      PaperProps={{
        style: {
          width: '80%',
        },
      }}
      transitionDuration={200}
      {...drawerProps}
    >
      <Box>
        {task && (
          <>
            {policy && <PolicyHeader policy={policy} task={task} />}
            {metric && <MetricHeader metric={metric} task={task} />}

            {!task.governanceEntityId && (
              <Box
                className={classes.header}
                style={{ marginBottom: theme.spacing(2) }}
              >
                <Box style={{ display: 'flex', alignItems: 'center' }}>
                  <DetailIcon status={task.status} />
                  <Typography variant="h6">
                    <strong style={{ color: theme.palette.primary.main }}>
                      {task.name}
                    </strong>
                  </Typography>
                </Box>
              </Box>
            )}
            {!task.governanceEntityId && (
              <>
                {task.errors.length > 0 && (
                  <ErrorBoxWrapper errors={task.errors} />
                )}
              </>
            )}
            {(loadingPolicy || loadingMetric) && <Progress />}
            {(metricError || policyError) && (
              <ErrorPanel error={metricError || policyError!} />
            )}

            {metric && (
              <GovernanceTestOverviewMetric
                kind="tabs"
                metric={metric}
                test={{
                  errors: task.errors,
                  content: task.validatedDescriptor || '',
                  name: task.displayName,
                }}
                infoContent={
                  <Box style={{ display: 'flex', gap: 50 }}>
                    <WbTooltip title={task.metricSpecificResult?.value || ''}>
                      <Box>
                        <GenericField
                          label="Value"
                          value={roundWithoutApprossimation(
                            2,
                            task.metricSpecificResult?.value,
                          )}
                        />
                      </Box>
                    </WbTooltip>

                    <>
                      <GenericField
                        label="Latest Update"
                        value={format(
                          parseISO(task.startTime),
                          'yyyy/MM/dd HH:mm:ss',
                        )}
                      />

                      <GenericField
                        label="Label"
                        value={
                          task.metricSpecificResult?.threshold?.label ||
                          'No Label'
                        }
                      />
                      {task.metricSpecificResult?.details && (
                        <GenericField
                          label="Test Detail"
                          value={
                            <CodeField
                              title="Test Detail"
                              value={JSON.stringify(
                                task.metricSpecificResult?.details,
                                null,
                                4,
                              )}
                            />
                          }
                        />
                      )}
                    </>
                  </Box>
                }
              />
            )}

            {policy && (
              <GovernanceTestOverviewPolicy
                kind="tabs"
                policy={policy}
                infoContent={
                  <Box style={{ display: 'flex', gap: 50 }}>
                    <>
                      <GenericField
                        label="Result Status"
                        value={snakeCaseToTitleCase(task.status.toLowerCase())}
                      />
                      <GenericField
                        label="Latest Update"
                        value={format(
                          parseISO(task.startTime),
                          'yyyy/MM/dd HH:mm:ss',
                        )}
                      />
                      {task.details && (
                        <GenericField
                          label="Test Detail"
                          value={
                            <CodeField
                              title="Test Detail"
                              value={JSON.stringify(task.details, null, 4)}
                            />
                          }
                        />
                      )}
                    </>
                  </Box>
                }
                test={{
                  errors: task.errors,
                  content: task.validatedDescriptor || '',
                  name: task.displayName,
                }}
              />
            )}
          </>
        )}
      </Box>
    </Drawer>
  );
};
